<template>
    <div class="col-md-12 mx-auto">
        <div class="card mt-12">
            <div class="card-header bg-primary text-white">
                <page-title :title="($t(titleKey))"></page-title>
            </div>

            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="form-inline col-6"></div>

                    <div class="input-group">
                        <input v-model="searchQuery"
                               v-on:keyup.enter.stop="massSearch"
                               v-on:keyup.esc.stop="clearFilters"
                               type="text"
                               class="form-control"
                               aria-label="Mass search input"
                               aria-describedby="massSearch" />
                        <button class="btn btn-secondary" @click.stop="massSearch">
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button class="btn btn-secondary" @click.stop="clearFilters">
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size="defaultGridRows"
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError"
                                  :requestEnd="datasorceRequestEnd"></kendo-datasource>

                <kendo-grid ref="grid"
                            data-source-ref="datasource1"
                            :pageable="customGridOptions.pageable"
                            :navigatable="customGridOptions.navigatable"
                            :editable="customGridOptions.editable"
                            :filterable="customGridOptions.filterable"
                            :reorderable="customGridOptions.reorderable"
                            :resizable="customGridOptions.resizable"
                            :groupable="customGridOptions.groupable"
                            :allow-copy="customGridOptions.allowCopy"
                            :column-menu="customGridOptions.columnMenu"
                            :sortable-mode="customGridOptions.sortable.mode"
                            :sortable-allow-unsort="customGridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="customGridOptions.sortable.showIndexes"
                            :selectable="customGridOptions.selectable"
                            :scrollable="customGridOptions.scrollable"
                            :toolbar="toolbar">
                    <kendo-grid-column :field="'id'" :title="$t('registerStatus.gridHeaders.id')" :expandable="true" :hidden="true"></kendo-grid-column>
                    <kendo-grid-column :field="'name'" :title="$t('registerStatus.gridHeaders.name')"></kendo-grid-column>
                    <kendo-grid-column :field="'nameEn'" :title="$t('registerStatus.gridHeaders.nameEn')"></kendo-grid-column>
                    <kendo-grid-column :field="'isCompleted'" :template="isCompletedCellTemplate" :title="$t('registerStatus.gridHeaders.isCompleted')"></kendo-grid-column>
                    <kendo-grid-column :field="'deactivated'" :title="$t('registerStatus.gridHeaders.deactivated')" :hidden="true"></kendo-grid-column>
                    <kendo-grid-column :field="'active'" :template="activeCellTemplate" :title="$t('registerStatus.gridHeaders.active')"></kendo-grid-column>
                    <kendo-grid-column :command="command" :title="$t('actions')"></kendo-grid-column>
                    <kendo-grid-column :title="' '"
                                    field="btnsColumn"
                                    :sortable="false"
                                    :filterable="false"
                                    :template="btnsTemplate">
                    </kendo-grid-column>
                </kendo-grid>
            </div>
        </div>
        <JsonFormatter
            v-model="dataReferences"
            :show.sync="showDataReferences"
            :titleKey="'buttons.links.tooltipText'"
            @onClose="onDataReferencesClose"
        >
        </JsonFormatter>
    </div>
</template>

<script>
    import Vue from 'vue';
    import PageTitle from '@/components/common/PageTitle';
    import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
    import dataReferencesService from "@/services/dataReferences.service";
    import JsonFormatter from "@/components/common/json.formatter.modal.vue";
    import { mapGetters } from 'vuex';

    export default {
        components: {
            PageTitle,
            JsonFormatter
        },
        props: {
            showResizeIcon: {
                default: false,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            }
        },
        data() {
            return {
                componentName: 'registerStatus',
                titleKey: 'registerStatus.title',
                searchQuery: '',
                defaultGridRows: this.$store.getters.defaultGridRows,
                schemaModelFields: {
                    id: {
                        name: 'id',
                        type: 'number',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'registerStatus.gridHeaders.id'
                    },
                    name: {
                        name: 'name',
                        type: 'string',
                        editable: true,
                        nullable: false,
                        visible: true,
                        titleKey: 'registerStatus.gridHeaders.name',
                        validation: { required: true }
                    },
                    nameEn: {
                        name: 'nameEn',
                        type: 'string',
                        editable: true,
                        nullable: true,
                        visible: true,
                        titleKey: 'registerStatus.gridHeaders.nameEn'
                    },
                    isCompleted: {
                        name: 'isCompleted',
                        type: 'boolean',
                        editable: true,
                        nullable: false,
                        visible: true,
                        titleKey: 'registerStatus.gridHeaders.isCompleted'
                    },
                    active: {
                        name: 'active',
                        type: 'boolean',
                        editable: false,
                        nullable: false,
                        visible: true,
                        titleKey: 'registerStatus.gridHeaders.active'
                    },
                    deactivated: {
                        name: 'deactivated',
                        type: 'boolean',
                        editable: true,
                        nullable: false,
                        visible: false,
                        titleKey: 'registerStatus.gridHeaders.deactivated'
                    }
                },
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/registerStatus/list',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    create: {
                        url: this.$store.getters.baseUrl + '/api/registerStatus/create',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    update: {
                        url: this.$store.getters.baseUrl + '/api/registerStatus/update',
                        dataType: 'json',
                        type: 'PUT',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                working: false,
                dataReferences: "",
                showDataReferences: false,
            };
        },
        methods: {
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) };
                } else {
                    // Викаме POST action за листване на номенклатурите и трябва да сериализираме
                    // options. Така се предава информация за take, skip , page и списък с обекти
                    // описващи сортирането
                    return kendo.stringify(options);
                }
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration
                });
            },
            activeCellTemplate: function (dataItem) {
                let templateHtml =
                    '<span class="badge ' +
                    (dataItem.active ? 'badge-success' : 'badge-danger') +
                    '">' +
                    kendo.htmlEncode(dataItem.active ? this.$t('yes') : this.$t('no')) +
                    '</span>';

                return templateHtml;
            },
            isCompletedCellTemplate: function (dataItem) {
                let templateHtml =
                    '<span class="badge ' +
                    (dataItem.isCompleted ? 'badge-success' : 'badge-secondary') +
                    '">' +
                    kendo.htmlEncode(dataItem.isCompleted ? this.$t('yes') : this.$t('no')) +
                    '</span>';

                return templateHtml;
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            datasorceRequestEnd(e) {
                if (e.type === 'create' && e.response) {
                    e.sender.read();
                } else if (e.type === 'update' && e.response) {
                    e.sender.read();
                }
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        parentComponent: this.$refs.grid,
                        btns:
                            [
                                {
                                    name: 'dataReferences',
                                    text: "",
                                    tooltip: this.$t('buttons.links.tooltipText'),
                                    class: 'btn btn-info',
                                    icon: "fas fa-link",
                                    show: this.$store.getters.claims.dataReferences.read,
                                    clickHandler: this.dataReferencesBtnClick
                                }
                            ]
                    }
                };
            },
            dataReferencesBtnClick(args) {
                if (!this.claims.dataReferences.read) {
                    return this.$router.push(`/unauthorized`);
                }

                return this.getDataReferences(args.id);
            },
            getDataReferences(id) {
                dataReferencesService
                    .checkReferences("N", "RegisterStatus", id, this.dataReferencesListCount, true)
                    .then((response) => {
                        if (response.data) {
                            this.dataReferences = response.data;
                        }

                        this.showDataReferences = true;
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            },
            onDataReferencesClose() {
                this.dataReferences = "";
                this.showDataReferences = false;
            }
        },
        computed: {
            ...mapGetters(['claims', 'gridOptions', 'isEnVersion', 'dataReferencesListCount']),
            token: function () {
                return this.$store.getters.authToken;
            },
            toolbar: function () {
                return [{ name: 'create', text: this.$t('buttons.create.btnText') }];
            },
            command: function () {
                return this.claims.nomenclatureTypes.create ? [{ name: 'edit', text: this.$t('buttons.edit.btnText') }] : [];
            },
            customGridOptions() {
                let options = {
                    ...this.gridOptions,
                    editable: this.claims.nomenclatureTypes.update
                        ? {
                            mode: 'popup',
                            window: {
                                title: this.$t('person.gridTitle'),
                                animation: true
                            },
                            move: true
                        }
                        : false
                }

                return options
            }
        }
    };
</script>

<style scoped>
</style>
